import { faHeart, faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import {
    faExclamationTriangle,
    faEyeSlash,
    faFileTimes,
    faPencil,
    faThumbtack,
    faTrafficLightStop,
    faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import cx from 'classnames';
import { Location } from 'history';
import memoize from 'lodash-es/memoize';
import {
    BLOG_POST_EDIT_MENU,
    BLOG_REPORT_POST,
    DELETE,
    GROUP_DONT_SHOW_POSTS_FROM_group,
    GROUP_LEAVE_group,
    PB_IGNORE_POSTS_FROM_USER_username,
    PIN_POST,
    REMOVE_FROM_FEED,
    RETURN_TO_FEED,
    SEND_HEART_TO_username,
    SHOW_USERS_WHO_LIKED_THIS_POST,
    UNPIN_POST,
} from 'mk/autogenerated/translations/BlogPostMenu.be8e1aec63c4533d5e730757b6492956'
import { isAuthenticated } from 'mk/bazaar/common/userUtils';
import { BLOG_POST_MENU_MORE } from 'mk2/apps/blogs/helpers';
import { BlogFeedItemEntity, BlogPostEntity } from 'mk2/apps/blogs/schemas';
import { blogsEntityLikersUrl, blogsPostEditUrl } from 'mk2/apps/blogs/urls';
import { canBeSetSticky } from 'mk2/apps/blogs/utils';
import PostMenuTrigger from 'mk2/apps/forum/components/PostMenuTrigger';
import { groupsEntityLikersUrl, groupsPostEditUrl } from 'mk2/apps/groups/urls';
import { heartsUrl } from 'mk2/apps/hearts/urls';
import MenuItem from 'mk2/components/menu/MenuItem';
import { ComplaintTargetType } from 'mk2/containers/Complaint/Complaint.schemas';
import Menu from 'mk2/containers/Menu/Menu';
import { url } from 'mk2/helpers/urls';
import { UserEntity } from 'mk2/schemas';
import { interpolate } from 'mk2/services/i18n';
import React from 'react';

import styles from './BlogPostMenu.mscss';

interface OwnProps {
    className?: string;
    hasDelete: boolean;
    hasEdit: boolean;
    hasLeave: boolean;
    hasHideGroupInFeed: boolean;
    hasReport: boolean;
    isAdmin: boolean;
    isAuthor: boolean;
    isModerator: boolean;
    post: BlogPostEntity;
    parentFeedItem?: BlogFeedItemEntity;
    requestUser: UserEntity;
    location: Location;

    onChangeSticky(postId: number, sticky: boolean);
    onDelete(postId: number);
    onGroupLeave?(groupId: number);
    onHideGroupInFeed?(groupId: number);
    onIgnoreUser?(username: string);
    onReport(entityType: ComplaintTargetType, postId: number);
    onSendHeart?();
    onSetDisplayingAtFeed?(postId: number, displaying: boolean);
    onToggleMenuMore(postId: number);
}

type Props = OwnProps;

class BlogPostMenu extends React.PureComponent<Props> {

    private handleIgnore = memoize((username) => () => {
        const { onIgnoreUser } = this.props;
        onIgnoreUser(username);
    });

    public render() {
        const {
            className, hasDelete, hasEdit, hasHideGroupInFeed, hasLeave, hasReport, isAdmin, isAuthor, isModerator,
            location, onChangeSticky, onIgnoreUser, parentFeedItem, post, requestUser,
        } = this.props;

        const blogsPostEditUrlVal = blogsPostEditUrl(post);

        const showReport = hasReport && !post.deletedAt && !isAuthor;
        const showEdit = hasEdit && (isAuthor || isAdmin) && !post.deletedAt && !!blogsPostEditUrlVal;
        const showDelete = hasDelete && (isAuthor || isModerator || isAdmin) && !post.deletedAt;
        const showWhoLiked = isAuthenticated(requestUser) && post.likeable.likesCount > 0;
        const showSticky = !!post.group && (isModerator || isAdmin) && canBeSetSticky(post) && onChangeSticky;
        const showIgnore = isAuthenticated(requestUser) && !post.group && onIgnoreUser;
        const showSetDisplayingAtFeed = !post.group && isAdmin;

        const role = isAuthor ? null : isModerator ? 'moderator' : isAdmin ? 'admin' : null;

        return (showReport || showEdit || showDelete) ? (
            <Menu
                className={cx(styles.BlogPostMenu, className)}
                menuId={BLOG_POST_MENU_MORE(post.id)}
                trigger={<PostMenuTrigger onClick={this.handleOnToggleMenuMore} />}
                triggerShadow={false}
            >

                {showReport && (
                    <MenuItem
                        label={BLOG_REPORT_POST}
                        icon={faExclamationTriangle}
                        onClick={this.handleOnReport}
                    />
                )}

                {!isAuthor && isAuthenticated(requestUser) && (
                    <MenuItem
                        label={interpolate(SEND_HEART_TO_username, { username: post.author.username })}
                        icon={faHeart}
                        link={url(heartsUrl, {}, {
                            to: post.author.username,
                            next: location.pathname,
                        })}
                        asAnchor
                    />
                )}

                {
                    showIgnore && parentFeedItem &&
                    parentFeedItem.user.username !== post.author.username &&
                    parentFeedItem.user.username !== requestUser.username && (
                    <MenuItem
                        label={interpolate(PB_IGNORE_POSTS_FROM_USER_username, { username: parentFeedItem.user.username })}
                        icon={faEyeSlash}
                        onClick={this.handleIgnore(parentFeedItem.user.username)}
                    />
                )}

                {showIgnore && (post.author.username !== requestUser.username) && (
                    <MenuItem
                        label={interpolate(PB_IGNORE_POSTS_FROM_USER_username, { username: post.author.username })}
                        icon={faEyeSlash}
                        onClick={this.handleIgnore(post.author.username)}
                    />
                )}

                {showIgnore && post.sharedPost && (post.sharedPost.author.username !== requestUser.username) && (
                    <MenuItem
                        label={interpolate(PB_IGNORE_POSTS_FROM_USER_username, { username: post.sharedPost.author.username })}
                        icon={faEyeSlash}
                        onClick={this.handleIgnore(post.sharedPost.author.username)}
                    />
                )}

                {showWhoLiked && (
                    <MenuItem
                        label={SHOW_USERS_WHO_LIKED_THIS_POST}
                        icon={faThumbsUp}
                        link={post.group
                            ? url(groupsEntityLikersUrl, { type: 'post', entityId: post.id })
                            : url(blogsEntityLikersUrl, { type: 'short-message', entityId: post.id })
                        }
                    />
                )}

                {showSticky && (
                    <MenuItem
                        key="sticky"
                        label={post.sticky ? UNPIN_POST : PIN_POST}
                        icon={faThumbtack}
                        className={cx(
                            isModerator
                                ? styles['BlogPostMenu__item--moderator']
                                : styles['BlogPostMenu__item--admin'],
                        )}
                        onClick={this.handleOnChangeSticky}
                    />
                )}

                {showEdit && (
                    <MenuItem
                        className={role && styles[`BlogPostMenu__item--${role}`]}
                        label={BLOG_POST_EDIT_MENU}
                        icon={faPencil}
                        link={post.group
                            ? url(groupsPostEditUrl, { postId: post.id })
                            : url(blogsPostEditUrlVal, {
                                username: post.author.username,
                                postSlug: post.slug,
                                postId: post.id,
                            })
                        }
                    />
                )}

                {showDelete && (
                    <MenuItem
                        className={role && styles[`BlogPostMenu__item--${role}`]}
                        label={DELETE}
                        icon={faTrash}
                        onClick={this.handleOnDelete}
                    />
                )}

                {showSetDisplayingAtFeed && (
                    <MenuItem
                        className={role && styles[`BlogPostMenu__item--${role}`]}
                        label={post.isDisplayingAtFeed ? REMOVE_FROM_FEED : RETURN_TO_FEED}
                        onClick={post.isDisplayingAtFeed ? this.handleRemoveFromFeed : this.handleReturnToFeed}
                    />
                )}

                {hasHideGroupInFeed && post.group && (
                    <MenuItem
                        label={interpolate(GROUP_DONT_SHOW_POSTS_FROM_group, { group: post.group.name })}
                        icon={faTrafficLightStop}
                        onClick={this.handleHideGroupInFeed}
                    />
                )}

                {hasLeave && post.group && (
                    <MenuItem
                        label={interpolate(GROUP_LEAVE_group, { group: post.group.name })}
                        icon={faFileTimes}
                        onClick={this.handleLeave}
                    />
                )}
            </Menu>
        ) : null;
    }

    private handleOnToggleMenuMore = () => {
        const { post, onToggleMenuMore } = this.props;
        onToggleMenuMore(post.id);
    };

    private handleOnChangeSticky = () => {
        const { post, onChangeSticky } = this.props;
        onChangeSticky(post.id, !post.sticky);
    };

    private handleOnReport = () => {
        const { post, onReport } = this.props;
        onReport(ComplaintTargetType.PhotoblogPost, post.id);
    };

    private handleOnDelete = () => {
        const { post, onDelete } = this.props;
        onDelete(post.id);
    };

    private handleRemoveFromFeed = () => {
        const { post, onSetDisplayingAtFeed } = this.props;
        onSetDisplayingAtFeed(post.id, false);
    };

    private handleReturnToFeed = () => {
        const { post, onSetDisplayingAtFeed } = this.props;
        onSetDisplayingAtFeed(post.id, true);
    };

    private handleHideGroupInFeed = () => {
        const { post, onHideGroupInFeed } = this.props;
        onHideGroupInFeed(post.group.id);
    };

    private handleLeave = () => {
        const { post, onGroupLeave } = this.props;
        onGroupLeave(post.group.id);
    };
}

export default BlogPostMenu;
